/**
 * Website write tools — let the planner edit the public wedding-site config:
 * free-text section copy, section visibility, the RSVP deadline, and the
 * publish flip. All writes go through `updateWebsiteConfig` so the existing
 * premium gate (defense-in-depth before the canonical server gate) applies.
 */

import { Schema } from "firebase/ai";
import {
  ALL_WEBSITE_SECTIONS,
  DEFAULT_VISIBLE_SECTIONS,
  WebsiteSectionIdSchema,
  type WebsiteSectionId,
} from "@amazing-wedding/shared-types";
import { PlanRequiredError } from "../operations";
import type { PlannerTool } from "./registry";
import { asBoolean, asEnum, asString, normalizeDate } from "./helpers";

function publicSiteUrl(handle: string | undefined | null): string | null {
  if (!handle) return null;
  return `/w/${handle}`;
}

function sectionLabel(id: WebsiteSectionId): string {
  return id;
}

export const websiteTools: PlannerTool[] = [
  {
    declaration: {
      name: "get_website_status",
      description:
        "Read the current state of the couple's public wedding website: whether it's published, the public URL handle, RSVP deadline, which sections are visible vs hidden, and which sections already have couple-written content. Call this before suggesting edits to the site.",
    },
    destructive: false,
    describe: () => "Stan strony WWW",
    execute: async (_args, ctx) => {
      const w = ctx.wedding();
      const config = w.website;
      const visible: WebsiteSectionId[] = config?.visibleSections ?? DEFAULT_VISIBLE_SECTIONS;
      const hidden = ALL_WEBSITE_SECTIONS.filter((s) => !visible.includes(s));
      const content = config?.content ?? {};
      const sectionsWithContent = Object.entries(content)
        .filter(([, text]) => typeof text === "string" && text.trim() !== "")
        .map(([id]) => id);
      return {
        published: config?.published ?? false,
        publishedAt: config?.publishedAt ?? null,
        handle: w.handle ?? null,
        publicUrl: publicSiteUrl(w.handle),
        rsvpDeadline: config?.rsvpDeadline ?? null,
        primaryLocale: w.settings?.language ?? "pl",
        enabledLocales: config?.enabledLocales ?? [],
        theme: config?.theme ?? "classic",
        visibleSections: visible,
        hiddenSections: hidden,
        sectionsWithContent,
      };
    },
  },
  {
    declaration: {
      name: "update_website_section_content",
      description:
        "Set the free-text content of a single section on the public wedding site (e.g. 'story', 'venue', 'transport', 'hotels', 'dresscode', 'registry', 'faq', 'rsvp'). Pass an empty string to clear the section back to the built-in default. Writes the primary locale only; per-locale overrides remain a UI-only feature.",
      parameters: Schema.object({
        properties: {
          sectionId: Schema.enumString({
            enum: [...ALL_WEBSITE_SECTIONS],
            description: "Section to edit.",
          }),
          text: Schema.string({
            description: "New free-text content. Pass an empty string to clear.",
          }),
        },
      }),
    },
    destructive: false,
    describe: (args) => {
      const id = asEnum(args.sectionId, ALL_WEBSITE_SECTIONS);
      return id ? `Edycja sekcji strony: ${sectionLabel(id)}` : "Edycja sekcji strony";
    },
    execute: async (args, ctx) => {
      const sectionId = asEnum(args.sectionId, ALL_WEBSITE_SECTIONS);
      if (!sectionId) throw new Error("A valid 'sectionId' is required.");
      const text = typeof args.text === "string" ? args.text : "";
      const trimmed = text.trim();
      await ctx.operations.website.updateWebsiteConfig(ctx.weddingId, {
        content: { [sectionId]: trimmed === "" ? null : trimmed },
      });
      return { ok: true, sectionId, cleared: trimmed === "" };
    },
  },
  {
    declaration: {
      name: "toggle_website_section",
      description:
        "Show or hide a section on the public wedding site. Useful when the couple wants to remove a section they're not using (e.g. 'hide the dresscode') or restore one they previously hid.",
      parameters: Schema.object({
        properties: {
          sectionId: Schema.enumString({
            enum: [...ALL_WEBSITE_SECTIONS],
            description: "Section to toggle.",
          }),
          visible: Schema.boolean({ description: "true = show, false = hide." }),
        },
      }),
    },
    destructive: false,
    describe: (args) => {
      const id = asEnum(args.sectionId, ALL_WEBSITE_SECTIONS) ?? "?";
      const v = asBoolean(args.visible);
      return v === false ? `Ukrycie sekcji: ${id}` : `Pokazanie sekcji: ${id}`;
    },
    execute: async (args, ctx) => {
      const sectionId = asEnum(args.sectionId, ALL_WEBSITE_SECTIONS);
      if (!sectionId) throw new Error("A valid 'sectionId' is required.");
      const visible = asBoolean(args.visible);
      if (visible === undefined) throw new Error("Missing required boolean 'visible'.");
      const current = ctx.wedding().website?.visibleSections ?? DEFAULT_VISIBLE_SECTIONS;
      const set = new Set(current);
      if (visible) set.add(sectionId);
      else set.delete(sectionId);
      const next = ALL_WEBSITE_SECTIONS.filter((s) => set.has(s));
      // Defensive: keep parsing in lock-step with the schema's enum.
      WebsiteSectionIdSchema.parse(sectionId);
      await ctx.operations.website.updateWebsiteConfig(ctx.weddingId, { visibleSections: next });
      return { ok: true, sectionId, visible };
    },
  },
  {
    declaration: {
      name: "set_rsvp_deadline",
      description:
        "Set the cut-off date after which the public RSVP form shows a 'closed' message instead of accepting submissions. Pass an ISO-8601 timestamp, or pass an empty string to remove the deadline.",
      parameters: Schema.object({
        properties: {
          deadline: Schema.string({
            description:
              "ISO-8601 timestamp, e.g. '2026-05-15' or '2026-05-15T20:00:00.000Z'. Empty string clears the deadline.",
          }),
        },
      }),
    },
    destructive: false,
    describe: (args) => {
      const v = asString(args.deadline);
      return v ? `Termin RSVP: ${v}` : "Usunięcie terminu RSVP";
    },
    execute: async (args, ctx) => {
      const raw = typeof args.deadline === "string" ? args.deadline.trim() : "";
      const normalized = raw === "" ? null : normalizeDate(raw);
      if (raw !== "" && normalized == null) {
        throw new Error("Invalid date. Use ISO-8601, e.g. 2026-05-15.");
      }
      await ctx.operations.website.updateWebsiteConfig(ctx.weddingId, { rsvpDeadline: normalized });
      return { ok: true, rsvpDeadline: normalized };
    },
  },
  {
    declaration: {
      name: "set_website_published",
      description:
        "Publish the wedding site (make it visible at /w/:handle) or unpublish it (take it down). Publishing requires a Premium plan; the server-side gate is the canonical authority. Always state plainly that the site will become public (or be hidden) before the user confirms.",
      parameters: Schema.object({
        properties: {
          published: Schema.boolean({
            description: "true = publish, false = unpublish.",
          }),
        },
      }),
    },
    destructive: true,
    describe: (args) =>
      asBoolean(args.published) ? "Publikacja strony" : "Cofnięcie publikacji strony",
    execute: async (args, ctx) => {
      const published = asBoolean(args.published);
      if (published === undefined) throw new Error("Missing required boolean 'published'.");
      try {
        await ctx.operations.website.updateWebsiteConfig(ctx.weddingId, { published });
      } catch (err) {
        if (err instanceof PlanRequiredError) {
          throw new Error(
            "Publikacja strony jest dostępna tylko w planie Premium. Zaproponuj przejście na Premium.",
          );
        }
        throw err;
      }
      return { ok: true, published };
    },
  },
];
